import { useEffect, useRef, useState } from "react";
import { api, wsUrl } from "./api";

function diffEvents(prev, next) {
  const events = [];
  if (!prev || !next) return events;
  if (next.pending_claim_id && next.pending_claim_id !== prev.pending_claim_id) {
    events.push({ type: "bingo_claim_polled", claim_id: next.pending_claim_id });
  }
  for (const stage of ["row", "two_rows", "full"]) {
    const w = next.winners?.[stage];
    if (w && !prev.winners?.[stage]) {
      events.push({ type: "claim_verified", approve: true, claim_id: w.claim_id, stage, display_name: w.display_name, ticket_id: w.ticket_id });
    }
  }
  return events;
}

export function useGameSocket(gameId, onEvent) {
  const [game, setGame] = useState(null);
  const [viewers, setViewers] = useState(0);
  const [connected, setConnected] = useState(false);
  const [polling, setPolling] = useState(false);
  const wsRef = useRef(null);
  const connectedRef = useRef(false);
  const gameRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const applyGame = (g, fromPoll) => {
    if (!g) return;
    if (fromPoll) diffEvents(gameRef.current, g).forEach((ev) => onEventRef.current && onEventRef.current(ev));
    gameRef.current = g;
    setGame(g);
  };

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    let pingInterval = null;

    const connect = () => {
      let ws;
      try { ws = new WebSocket(wsUrl(gameId)); } catch (e) { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        connectedRef.current = true;
        setConnected(true);
        setPolling(false);
        pingInterval = setInterval(() => { try { ws.send("ping"); } catch (e) { /* ignore */ } }, 25000);
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "game_update") {
            applyGame(msg.game, false);
            if (typeof msg.viewers === "number") setViewers(msg.viewers);
          } else if (msg.type === "viewers") {
            setViewers(msg.count);
          }
          if (onEventRef.current) onEventRef.current(msg);
        } catch (err) { /* ignore non-json */ }
      };
      ws.onclose = () => {
        connectedRef.current = false;
        setConnected(false);
        if (pingInterval) clearInterval(pingInterval);
        if (!cancelled) setTimeout(connect, 1500);
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
    };
    connect();

    const poll = async () => {
      if (cancelled || connectedRef.current) return;
      try {
        const r = await api.get(`/games/${gameId}`);
        if (cancelled || connectedRef.current) return;
        setPolling(true);
        applyGame(r.data, true);
      } catch (e) { /* ignore */ }
    };
    const pollTimeout = setTimeout(poll, 1200);
    const pollInterval = setInterval(poll, 2500);

    return () => {
      cancelled = true;
      clearTimeout(pollTimeout);
      clearInterval(pollInterval);
      if (pingInterval) clearInterval(pingInterval);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    };
     
  }, [gameId]);

  return { game, viewers, connected, polling };
}
